import {Listbox} from '@headlessui/react'
import classNames from 'classnames'
import React, {Fragment} from 'react'

import ChevronIcon from '../../shared/chevronIcon/ChevronIcon'
import Icon, {Icons} from '../icons/Icon'

export interface DropdownOption {
  id: string;
  value: string;
}

export type DropdownTypes = 'primary' | 'secondary' | 'text';
export type DropdownSizes = 'sm' | 'md' | 'lg';

export interface DropdownProps {
  options: DropdownOption[];
  placeholderText?: string;
  leadingIcon?: Icons;
  selectedValue: DropdownOption | null;
  setSelectedValue: (option: DropdownOption) => void;
  resetOption?: boolean;
  resetOptionText?: string;
  dropdownType?: DropdownTypes;
  size?: DropdownSizes;
  fullWidth?: boolean;
  disabled?: boolean;
}

const renderDisplayValue = (placeholder: string | undefined, value: string) => {
  if (!placeholder) return value
  return value || placeholder
}

const Dropdown = ({
  options,
  placeholderText,
  leadingIcon,
  selectedValue,
  setSelectedValue,
  resetOption = false,
  resetOptionText,
  dropdownType = 'primary',
  size = 'md',
  disabled = false,
  fullWidth = false
}: DropdownProps) => {
  const wrapperClassNames = classNames('relative', {
    'w-full': fullWidth,
    'before:content-[""] before:absolute before:inset-0 before:bg-kwota-neutral-100/60 pointer-events-none':
      disabled,
    'before:rounded-2xl': disabled && size === 'lg',
    'before:rounded-xl': disabled && size === 'md',
    'before:rounded-lg': disabled && size === 'sm'
  })

  const generateButtonClassNames = (open: boolean) =>
    classNames(
      'w-full flex items-center cursor-pointer overflow-hidden font-titillium focus:outline-none text-kwota-neutral-800 px-4',
      {
        'justify-between':
          dropdownType === 'primary' || dropdownType === 'secondary' || open,
        'justify-center': dropdownType === 'text' && !open,

        'bg-kwota-neutral-100 font-normal border':
          dropdownType === 'primary' || dropdownType === 'text',

        'border-kwota-neutral-300': dropdownType === 'primary' || open,
        'border-transparent': dropdownType === 'text' && !open,

        'bg-kwota-neutral-200 font-semibold': dropdownType === 'secondary',

        'rounded-t-2xl': size === 'lg' && open,
        'rounded-2xl': size === 'lg' && (!open || dropdownType === 'secondary'),

        'rounded-t-xl': size === 'md' && open,
        'rounded-xl': size === 'md' && (!open || dropdownType === 'secondary'),

        'rounded-t-lg': size === 'sm' && open,
        'rounded-lg': size === 'sm' && (!open || dropdownType === 'secondary'),

        'py-4 md:text-lg leading-6': size === 'lg',
        'py-3 md:text-base': size === 'md',
        'py-2 text-sm leading-4': size === 'sm',

        'w-full': fullWidth,
        'min-w-[224px]': !fullWidth,

        'pointer-events-none': disabled
      }
    )

  const dropdownClassNames = classNames(
    'absolute z-20 w-full bg-kwota-neutral-100 text-left py-1 right-0 left-0 mx-auto max-h-60 font-titillium font-normal focus:outline-none overflow-auto border-kwota-neutral-300',
    {
      '-mt-4':
        (dropdownType === 'primary' || dropdownType === 'text') &&
        size !== 'sm',
      '-mt-3':
        (dropdownType === 'primary' || dropdownType === 'text') &&
        size === 'sm',

      'text-lg': size === 'lg',
      'rounded-b-2xl':
        (size === 'lg' && dropdownType === 'primary') ||
        dropdownType === 'text',
      'rounded-2xl': size === 'lg' && dropdownType === 'secondary',

      'text-base': size === 'md',
      'rounded-b-xl':
        (size === 'md' && dropdownType === 'primary') ||
        dropdownType === 'text',
      'rounded-xl': size === 'md' && dropdownType === 'secondary',

      'text-sm': size === 'sm',
      'rounded-b-lg':
        (size === 'sm' && dropdownType === 'primary') ||
        dropdownType === 'text',
      'rounded-lg': size === 'sm' && dropdownType === 'secondary',

      'pt-4 border border-t-0':
        dropdownType === 'primary' || dropdownType === 'text',
      'mt-2 border': dropdownType === 'secondary'
    }
  )

  const optionClasses = classNames(
    'select-none cursor-pointer h-9 px-4 flex gap-2 items-center',
    {
      'font-semibold': dropdownType === 'secondary'
    }
  )

  const getOptionBgClassNames = (active: boolean) => classNames('block font-normal truncate', {
    'bg-kwota-primary-100': active
  })
  return (
    <Listbox
      as="div"
      value={selectedValue}
      onChange={setSelectedValue}
      className={wrapperClassNames}
    >
      {({open}) => {
        return (
          <>
            <Listbox.Button
              type="button"
              className={() => generateButtonClassNames(open)}
              aria-haspopup="listbox"
              aria-expanded="true"
            >
              <div className="flex items-center gap-2">
                {leadingIcon &&
                  <Icon icon={leadingIcon} size={size === 'lg' ? 'md' : size} />
                }
                <span className="block truncate">
                  {renderDisplayValue(
                    placeholderText,
                    selectedValue?.value || ''
                  )}
                </span>
              </div>
              <span className="flex items-center pointer-events-none">
                <ChevronIcon isOpen={open} />
              </span>
            </Listbox.Button>
            <div className="relative">
              {open &&
                <Listbox.Options as="ul" className={dropdownClassNames}>
                  <>
                    {resetOption &&
                      <Listbox.Option
                        as={Fragment}
                        value={null}
                      >
                        {({active}) => {
                          return (
                            <li
                              className={`${optionClasses} ${getOptionBgClassNames(active)}`}
                            >
                              <span className="pl-8 ">{resetOptionText || placeholderText || 'All options'}</span>
                            </li>
                          )
                        } }
                      </Listbox.Option>
                    }
                    {options.map(option => {
                      return (
                        <Listbox.Option
                          key={option.id}
                          as={Fragment}
                          value={option}
                        >
                          {({active, selected}) => {
                            return (
                              <li
                                className={`${optionClasses} ${getOptionBgClassNames(active)}`}
                              >
                                {selected &&
                                <Icon
                                  icon="tick"
                                  size={size === 'lg' ? 'md' : size}
                                  className="fill-kwota-primary-500"
                                />
                                }
                                <span
                                  className={`${
                                    !selected && 'pl-8'
                                  } block font-normal truncate`}
                                >
                                  {option.value}
                                </span>
                              </li>
                            )
                          }}
                        </Listbox.Option>
                      )
                    })}
                  </>
                </Listbox.Options>
              }
            </div>
          </>
        )
      }}
    </Listbox>
  )
}

export default Dropdown
